import React, {
  useEffect,
  useState,
  useRef,
  ComponentProps,
  useMemo,
  forwardRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";

import "./GameModal.scss";
import Logger from "../../../utils/logger";
import {
  GameFlowState,
  GameType,
  ROUTES,
  GamePlayEvents,
} from "../../../utils/constants";
import {
  gameFlowStateAtomWithStorage,
  gameTypeAtom,
} from "../../../jotai/atoms/games";
import { practiceGameEntryAtom } from "../../../jotai/atoms/tournament";
import {
  gamePlayEventDataAtom,
  gamePlayEventNameAtom,
} from "../../../jotai/atoms/games";
import { useWindowDimensions } from "../../../hooks/useWindowDimensions";
import { GamePlayer, GameMeta } from "../GamePlayer";
import { GetAppContent } from "../../GetAppModal";
import { TournamentsSvcGameOrientation, GameOrientation } from "@nestcoinco/hyper-api-gateway-api-client";
interface GameModalProps extends ComponentProps<"div"> {
  gameLink: string;
  gameId?: string;
  gameName?: string;
  hideInternalSidebar?: boolean;
  useFullscreenMode?: boolean;
  isEmbedded?: boolean;
  gameMeta?: GameMeta;
  practiceGameRedirectPath?: string;
  mobileVariant?: "iframe" | "game-player";
  mobileAutoStart?: boolean;
  isLoading?: boolean;
  orientation?: TournamentsSvcGameOrientation | GameOrientation;
}

interface DesktopGameInterfaceProps extends GameModalProps {
  gameMeta?: GameMeta;
}

const TIME_TO_SHOW_DEFAULT_RESULT = 2000;
const NESTCOIN_IDENTIFIER = "nestcoin";
const HYPER_IDENTIFIER = "hyper";

const DesktopGameInterface = forwardRef<
  HTMLDivElement,
  DesktopGameInterfaceProps
>(
  (
    {
      gameLink,
      gameId,
      gameName,
      className = "",
      hideInternalSidebar,
      useFullscreenMode,
      isEmbedded: _isEmbedded,
      gameMeta,
      practiceGameRedirectPath: _practiceGameRedirectPath,
      mobileVariant: _mobileVariant,
      mobileAutoStart: _mobileAutoStart,
      isLoading,
      orientation,
      ...restProps
    },
    _ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isPortrait = orientation === TournamentsSvcGameOrientation.PORTRAIT;

    return (
      <div
        className={`game-interface ${isExpanded ? "expanded" : ""} ${isPortrait ? "portrait" : ""} ${className}`}
        {...restProps}
      >
        <div className="game-area">
          <GamePlayer
            key={gameId}
            gameLink={gameLink}
            gameId={gameId}
            gameName={gameName}
            onExpandChange={setIsExpanded}
            useFullscreenMode={useFullscreenMode}
            gameMeta={gameMeta}
            isLoading={isLoading}
          />
        </div>
        {!hideInternalSidebar && !isExpanded && (
          <aside className="sidebar">
            <GetAppContent />
          </aside>
        )}
      </div>
    );
  }
);

DesktopGameInterface.displayName = "DesktopGameInterface";

function GameModal({
  gameLink,
  gameName,
  gameId,
  className = "",
  hideInternalSidebar,
  useFullscreenMode,
  isEmbedded,
  gameMeta,
  practiceGameRedirectPath: customPracticeGameRedirectPath,
  mobileVariant = "iframe",
  mobileAutoStart = false,
  isLoading,
  orientation,
  ...restProps
}: GameModalProps) {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [gameEvent, setGameEvent] = useAtom(gamePlayEventNameAtom);
  const [, setGameFlow] = useAtom(gameFlowStateAtomWithStorage);
  const gameType = useAtomValue(gameTypeAtom);
  const practiceGameEntry = useAtomValue(practiceGameEntryAtom);
  const [, setGamePlayEventData] = useAtom(
    gamePlayEventDataAtom
  );
  const [hasEnded, setHasEnded] = useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const isDesktop = windowWidth >= 1024;

  const practiceGameRedirectPath =
    customPracticeGameRedirectPath ||
    `${ROUTES.GAMES.ROOT}/${practiceGameEntry?.game.slug}${ROUTES.GAMES.PLAY_GAME}`;

  useEffect(() => {
    setGameEvent(undefined);
    setGamePlayEventData(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      if (
        !event.origin?.includes(NESTCOIN_IDENTIFIER) &&
        !event.origin?.includes(HYPER_IDENTIFIER)
      )
        return;

      if (event?.origin?.includes(HYPER_IDENTIFIER) && event?.data) {
        try {
          setGamePlayEventData(JSON.parse(event.data));
        } catch (e) {
          Logger.error(`Game event parse failed:, ${e}`);
        }
      }

      switch (true) {
        case event?.data?.includes(GamePlayEvents.GAME_START):
          setGameEvent(GamePlayEvents.GAME_START);
          break;
        case event?.data?.includes(GamePlayEvents.SDK_GAME_END_NO_INTERNET):
          navigate(ROUTES.ROOT.DASHBOARD);
          break;
        case event?.data?.includes(GamePlayEvents.PRACTICE_GAME_END):
          setGameEvent(GamePlayEvents.PRACTICE_GAME_END);
          break;
        case event?.data?.includes(GamePlayEvents.GAME_END):
          setGameEvent(GamePlayEvents.GAME_END);
          break;
        case event?.data?.includes(GamePlayEvents.GAME_FAILED):
          setGameEvent(GamePlayEvents.GAME_FAILED);
          break;
        case event?.data?.includes(GamePlayEvents.GAME_PAUSE):
          setGameEvent(GamePlayEvents.GAME_PAUSE);
          break;
        case event?.data?.includes(GamePlayEvents.SDK_CONNECTION_FAILED):
          setGameEvent(GamePlayEvents.SDK_CONNECTION_FAILED);
          break;
        case event?.data?.includes(GamePlayEvents.SDK_SESSION_TIMEOUT):
          setGameEvent(GamePlayEvents.SDK_SESSION_TIMEOUT);
          break;
        case event?.data?.includes(GamePlayEvents.SDK_WRONG_ORIENTATION):
          setGameEvent(GamePlayEvents.SDK_WRONG_ORIENTATION);
          break;
        case event?.data?.includes(GamePlayEvents.SDK_WRONG_PLATFORM):
          setGameEvent(GamePlayEvents.SDK_WRONG_PLATFORM);
          break;
        default:
          Logger.error(`Unhandled event: ${event.data}`);
      }
    };

    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [navigate, setGameEvent, setGamePlayEventData]);

  const canGameProceed = useMemo(
    () =>
      gameEvent !== GamePlayEvents.GAME_END &&
      gameEvent !== GamePlayEvents.GAME_FAILED &&
      gameEvent !== GamePlayEvents.PRACTICE_GAME_END &&
      gameEvent !== GamePlayEvents.SDK_CONNECTION_FAILED &&
      gameEvent !== GamePlayEvents.SDK_SESSION_TIMEOUT &&
      gameEvent !== GamePlayEvents.SDK_WRONG_PLATFORM &&
      gameEvent !== GamePlayEvents.SDK_WRONG_ORIENTATION &&
      gameEvent !== GamePlayEvents.SDK_GAME_END_NO_INTERNET,
    [gameEvent]
  );
  const hasGamePlayFailed = useMemo(
    () =>
      gameEvent === GamePlayEvents.GAME_FAILED ||
      gameEvent === GamePlayEvents.SDK_CONNECTION_FAILED ||
      gameEvent === GamePlayEvents.SDK_SESSION_TIMEOUT ||
      gameEvent === GamePlayEvents.SDK_WRONG_PLATFORM ||
      gameEvent === GamePlayEvents.SDK_WRONG_ORIENTATION ||
      gameEvent === GamePlayEvents.SDK_GAME_END_NO_INTERNET,
    [gameEvent]
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (canGameProceed) {
      return;
    }

    setHasEnded(true);
    if (gameType === GameType.PRACTICE && !hasGamePlayFailed) return;

    const currentFlow = hasGamePlayFailed
      ? GameFlowState.VIEWING_RESULTS_FOR_FAILED_GAME
      : GameFlowState.VIEWING_RESULTS;

    timeoutId = setTimeout(() => {
      setGameFlow(currentFlow);
    }, TIME_TO_SHOW_DEFAULT_RESULT);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [canGameProceed, gameEvent, gameType, hasGamePlayFailed, setGameFlow]);

  useEffect(() => {
    if (
      hasEnded &&
      gameType === GameType.PRACTICE &&
      (gameEvent === GamePlayEvents.PRACTICE_GAME_END || hasGamePlayFailed)
    ) {
      navigate(practiceGameRedirectPath);
    }
  }, [
    gameEvent,
    gameType,
    hasEnded,
    hasGamePlayFailed,
    navigate,
    practiceGameRedirectPath,
  ]);

  if (isDesktop) {
    return (
      <DesktopGameInterface
        gameLink={gameLink}
        gameId={gameId}
        gameName={gameName}
        className={`${isEmbedded ? "EmbeddedGameModal" : ""} ${className}`}
        hideInternalSidebar={hideInternalSidebar}
        useFullscreenMode={useFullscreenMode}
        isEmbedded={isEmbedded}
        gameMeta={gameMeta}
        isLoading={isLoading}
        orientation={orientation}
        {...restProps}
      />
    );
  }

  return (
    <div
      className={`game-interface MobileOnly ${isEmbedded ? "EmbeddedGameModal" : ""} ${className}`}
      {...restProps}
    >
      {mobileVariant === "game-player" ? (
        <GamePlayer
          key={gameId}
          gameLink={gameLink}
          gameId={gameId}
          gameName={gameName}
          useFullscreenMode={useFullscreenMode}
          gameMeta={gameMeta}
          autoStart={mobileAutoStart}
          isLoading={isLoading}
        />
      ) : (
        <iframe
          ref={iframeRef}
          src={gameLink}
          title="game-play"
          width="100%"
          height="100%"
        />
      )}
    </div>
  );
}

export default GameModal;
